import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Fingerprint, Trash2 } from 'lucide-react';
import { Botao, Cartao } from '../componentes/base';
import { Estado, Vazio } from '../componentes/estados';
import { Entrada, Rotulo } from '../componentes/formulario';
import { Confirmar, Modal } from '../componentes/sobreposicao';
import { TextoCortado } from '../componentes/texto-cortado';
import { useToast } from '../componentes/toast';
import { ErroRequisicao, del, get, mensagemDeErro, post } from '../nucleo/api';
import { criarPasskey, passkeyCancelada, suportaPasskey, type OpcoesCadastroPasskey, type PasskeyDaConta } from '../nucleo/assistentes/dados';
import { dataHora } from '../nucleo/formato';

/**
 * Fase 2 (W11): Minha conta > Segurança > Passkeys. A passkey confirma as ações sensíveis que o assistente prepara, com a
 * biometria do próprio aparelho (o MalgoOS guarda só a chave pública). Cadastrar pede a senha atual.
 */
const CHAVE = ['conta', 'passkeys'];

function DialogoNovaPasskey({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [erros, setErros] = useState<{ nome?: string; senha?: string; geral?: string }>({});
  const cadastrar = useMutation({
    mutationFn: async () => {
      const op = await post<OpcoesCadastroPasskey>('/auth/passkeys/opcoes');
      const cred = await criarPasskey(op);
      return post('/auth/passkeys', { nome: nome.trim(), senha_atual: senha, ...cred });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: CHAVE });
      toast.sucesso(t('assistentes.passkeys.cadastrada'));
      aoFechar();
    },
    onError: (e) => {
      if (passkeyCancelada(e)) return setErros({ geral: t('assistentes.passkeys.cancelada') });
      const campo = e instanceof ErroRequisicao ? (e.metaDoErro()?.campo as string | undefined) : undefined;
      if (campo === 'senha_atual') setErros({ senha: mensagemDeErro(e) });
      else setErros({ geral: mensagemDeErro(e) });
    },
  });
  function enviar() {
    const novos: typeof erros = {};
    if (!nome.trim()) novos.nome = t('erros.obrigatorio');
    if (!senha) novos.senha = t('erros.obrigatorio');
    setErros(novos);
    if (!novos.nome && !novos.senha) cadastrar.mutate();
  }
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('assistentes.passkeys.nova')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {t('comum.cancelar')}
          </Botao>
          <Botao variante="primario" icone={<Fingerprint className="size-4" aria-hidden />} carregando={cadastrar.isPending} onClick={enviar}>
            {t('assistentes.passkeys.criar')}
          </Botao>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-nova-passkey
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
      >
        <p className="text-sm text-tinta-2">{t('assistentes.passkeys.novaTexto')}</p>
        {erros.geral && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erros.geral}
          </p>
        )}
        <Rotulo id="passkey-nome" rotulo={t('assistentes.passkeys.nome')} erro={erros.nome} ajuda={t('assistentes.passkeys.nomeAjuda')}>
          <Entrada id="passkey-nome" maxLength={60} value={nome} aria-invalid={!!erros.nome || undefined} aria-describedby={erros.nome ? 'passkey-nome-erro' : undefined} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <Rotulo id="passkey-senha" rotulo={t('conta.seguranca.senha.atual')} erro={erros.senha}>
          <Entrada
            id="passkey-senha"
            type="password"
            autoComplete="current-password"
            value={senha}
            aria-invalid={!!erros.senha || undefined}
            aria-describedby={erros.senha ? 'passkey-senha-erro' : undefined}
            onChange={(e) => setSenha(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                enviar();
              }
            }}
          />
        </Rotulo>
      </form>
    </Modal>
  );
}

export function CartaoPasskeys() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const lista = useQuery({ queryKey: CHAVE, queryFn: () => get<{ itens: PasskeyDaConta[] }>('/auth/passkeys') });
  const [nova, setNova] = useState(false);
  const [remover, setRemover] = useState<PasskeyDaConta | null>(null);
  const tirar = useMutation({
    mutationFn: (id: string) => del(`/auth/passkeys/${id}`),
    onSuccess: async () => {
      setRemover(null);
      await qc.invalidateQueries({ queryKey: CHAVE });
      toast.sucesso(t('assistentes.passkeys.removida'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const suporta = suportaPasskey();
  return (
    <Cartao
      titulo={t('assistentes.passkeys.titulo')}
      acao={
        suporta && (
          <Botao tamanho="sm" icone={<Fingerprint className="size-4" aria-hidden />} onClick={() => setNova(true)}>
            {t('assistentes.passkeys.nova')}
          </Botao>
        )
      }
    >
      <div className="flex flex-col gap-3" data-cartao-passkeys>
        <p className="text-sm text-tinta-3">{t('assistentes.passkeys.texto')}</p>
        {!suporta && (
          <p role="status" className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">
            {t('assistentes.passkeys.semSuporte')}
          </p>
        )}
        <Estado consulta={lista}>
          {(d) =>
            d.itens.length === 0 ? (
              <Vazio icone={<Fingerprint className="size-5" aria-hidden />} titulo={t('assistentes.passkeys.vazioTitulo')} texto={t('assistentes.passkeys.vazioTexto')} />
            ) : (
              <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
                {d.itens.map((p) => (
                  <li key={p.id} className="flex min-w-0 items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <TextoCortado className="text-sm font-medium text-tinta-1">{p.nome}</TextoCortado>
                      <p className="text-xs text-tinta-3">
                        {t('assistentes.passkeys.criadaEm', { data: dataHora(p.criada_em) })}
                        {p.ultimo_uso_em ? ` · ${t('assistentes.passkeys.usadaEm', { data: dataHora(p.ultimo_uso_em) })}` : ''}
                      </p>
                    </div>
                    <Botao tamanho="sm" variante="perigo" icone={<Trash2 className="size-4" aria-hidden />} aria-label={t('assistentes.passkeys.removerNome', { nome: p.nome })} onClick={() => setRemover(p)}>
                      <span className="hidden sm:inline">{t('assistentes.passkeys.remover')}</span>
                    </Botao>
                  </li>
                ))}
              </ul>
            )
          }
        </Estado>
      </div>
      {nova && <DialogoNovaPasskey aoFechar={() => setNova(false)} />}
      <Confirmar
        aberto={!!remover}
        aoFechar={() => setRemover(null)}
        aoConfirmar={() => remover && tirar.mutate(remover.id)}
        carregando={tirar.isPending}
        perigoso
        titulo={t('assistentes.passkeys.removerTitulo', { nome: remover?.nome ?? '' })}
        texto={t('assistentes.passkeys.removerTexto')}
      />
    </Cartao>
  );
}
